(() => {
  if (customElements.get('oilogic-cart-drawer')) return;

class OilogicCartDrawer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.panel = this.querySelector('[data-cart-drawer-panel]');
    this.backdrop = this.querySelector('[data-cart-drawer-backdrop]');
    this.countEl = this.querySelector('[data-cart-drawer-count]');
    this.announcer = this.querySelector('[data-cart-drawer-announcer]');
    this.scrollBody = this.querySelector('[data-cart-drawer-scroll]');
    if (!this.panel) return;

    if (this.scrollBody && typeof ResizeObserver === 'function') {
      this.footerObserver = new ResizeObserver(() => this.updateFooterHeight());
      this.observeFooter();
    }

    this.sectionId = this.dataset.sectionId;
    this.upsellLimit = this.dataset.upsellLimit;
    this.recommendationsUrl = this.dataset.recommendationsUrl;

    this.isOpen = false;
    this.trigger = null;

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-drawer-toggle]');
      if (!toggle) return;
      event.preventDefault();
      this.open({ trigger: toggle });
      this.refresh(null, { announce: false });
    }, { signal });

    this.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(), { signal });
    });
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close(), { signal });
    }
    // The dialog wrapper covers the viewport (it centers the card), so a
    // click outside the card lands on it, not on the backdrop underneath.
    this.panel.addEventListener('click', (event) => {
      if (event.target === this.panel) this.close();
    }, { signal });

    this.panel.addEventListener('keydown', (event) => this.trapFocus(event), { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }, { signal });

    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-cart-drawer-remove]');
      if (removeBtn) this.removeLineItem(removeBtn.dataset.key);
    }, { signal });

    this.addEventListener('oilogic:quantity:change', (event) => {
      const { key, quantity } = event.detail || {};
      if (key) this.updateLineItem(key, quantity);
    }, { signal });

    document.addEventListener('oilogic:cart:added', (event) => {
      this.open({ trigger: document.activeElement });
      this.refresh(event.detail && event.detail.cart);
    }, { signal });
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.footerObserver) this.footerObserver.disconnect();
    document.body.style.overflow = '';
  }

  observeFooter() {
    if (!this.footerObserver) return;
    this.footerObserver.disconnect();
    const footer = this.querySelector('[data-cart-drawer-footer]');
    if (footer) this.footerObserver.observe(footer);
  }

  updateFooterHeight() {
    if (!this.scrollBody) return;
    const footer = this.querySelector('[data-cart-drawer-footer]');
    if (!footer) return;
    this.scrollBody.style.setProperty('--cart-drawer-footer-height', `${footer.offsetHeight + 16}px`);
  }

  open({ trigger } = {}) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.trigger = trigger || document.activeElement;

    this.backdrop.hidden = false;
    this.panel.hidden = false;
    requestAnimationFrame(() => this.classList.add('cart-drawer--open'));
    document.body.style.overflow = 'hidden';

    const focusable = this.focusables();
    (this.querySelector('[data-cart-drawer-close]') || focusable[0] || this.panel).focus();

    this.setTriggersExpanded(true);
    this.dispatchEvent(new CustomEvent('oilogic:cart:open', { bubbles: true, composed: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('cart-drawer--open');
    this.setTriggersExpanded(false);

    const finish = () => {
      clearTimeout(fallback);
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.panel.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.target === this.panel.querySelector('.cart-drawer__card')) finish();
    };
    this.panel.addEventListener('transitionend', onEnd);
    const fallback = setTimeout(finish, 400);

    document.body.style.overflow = '';
    this.dispatchEvent(new CustomEvent('oilogic:cart:close', { bubbles: true, composed: true }));
    if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus();
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  focusables() {
    return Array.from(
      this.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = this.focusables();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  announce(message) {
    if (!this.announcer || !message) return;
    this.announcer.textContent = '';
    requestAnimationFrame(() => {
      this.announcer.textContent = message;
    });
  }

  updateCount(cart) {
    if (!cart) return;
    if (this.countEl) {
      this.countEl.textContent = `(${cart.item_count})`;
      this.countEl.hidden = cart.item_count === 0;
    }
    // Keep the header badge(s) in sync too — adds go through the add-to-cart
    // component, but removals and quantity changes only pass through here.
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });
  }

  applyMarkup(html, names) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.querySelectorAll('[data-cart-drawer-dynamic]').forEach((node) => {
      const name = node.dataset.cartDrawerDynamic;
      if (names && !names.includes(name)) return;
      const next = doc.querySelector(`[data-cart-drawer-dynamic="${name}"]`);
      if (next) node.replaceWith(next);
    });
    this.observeFooter();
  }

  captureFocusContext() {
    const active = document.activeElement;
    if (!this.panel || !active || !this.panel.contains(active)) return null;

    const selector = active.closest('[data-quantity-decrease], [data-quantity-increase], [data-cart-drawer-remove]');
    if (!selector) return null;

    const key = active.closest('[data-key]')?.dataset.key || null;

    let action = 'remove';
    if (selector.hasAttribute('data-quantity-decrease')) action = 'decrease';
    else if (selector.hasAttribute('data-quantity-increase')) action = 'increase';

    return { key, action };
  }

  restoreFocus(context) {
    if (!context || !context.key) {
      this.focusFallback();
      return;
    }

    const key = window.CSS && CSS.escape ? CSS.escape(context.key) : context.key;
    const actionSelector = context.action === 'increase'
      ? `[data-quantity-increase]`
      : context.action === 'decrease'
        ? `[data-quantity-decrease]`
        : `[data-cart-drawer-remove]`;

    const scope = context.action === 'remove'
      ? this.querySelector(`[data-cart-drawer-remove][data-key="${key}"]`)
      : this.querySelector(`oilogic-quantity-selector[data-key="${key}"] ${actionSelector}`);

    if (scope) {
      scope.focus();
      return;
    }

    const items = this.querySelectorAll('[data-cart-drawer-remove]');
    if (items.length) {
      items[0].focus();
      return;
    }

    this.focusFallback();
  }

  focusFallback() {
    const closeBtn = this.querySelector('[data-cart-drawer-close]');
    if (closeBtn) {
      closeBtn.focus();
      return;
    }
    const focusable = this.focusables();
    (focusable[0] || this.panel).focus();
  }

  async refresh(cart, { announce = true } = {}) {
    try {
      let currentCart = cart;
      if (!currentCart) {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: this.controller.signal });
        if (!cartResponse.ok) return;
        currentCart = await cartResponse.json();
      }

      let url = `${window.location.pathname}?section_id=${this.sectionId}`;
      if (currentCart.items.length && this.recommendationsUrl) {
        const productId = currentCart.items[0].product_id;
        url = `${this.recommendationsUrl}?section_id=${this.sectionId}&product_id=${productId}&limit=${this.upsellLimit}&intent=related`;
      }

      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('cart-drawer-refresh-failed');

      this.applyMarkup(await response.text());
      this.updateCount(currentCart);
      if (announce) this.announce(this.dataset.announceAdded);
    } catch (error) {
      this.dispatchEvent(new CustomEvent('oilogic:cart:error', { bubbles: true, composed: true, detail: { error } }));
      this.announce(this.dataset.announceError);
    }
  }

  async updateLineItem(key, quantity) {
    const focusContext = this.captureFocusContext();
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error('cart-drawer-update-failed');

      const cart = await response.json();
      const sectionHtml = cart.sections && cart.sections[this.sectionId];
      // The Section Rendering API has no recommendations context, so leave the
      // upsell alone here or it would fall back to the generic product list.
      if (sectionHtml) this.applyMarkup(sectionHtml, ['items', 'footer']);
      this.updateCount(cart);
      if (focusContext) this.restoreFocus(focusContext);

      if (quantity === 0) {
        this.announce(this.dataset.announceRemoved);
        this.dispatchEvent(new CustomEvent('oilogic:cart:removed', { bubbles: true, composed: true, detail: { cart, key } }));
      } else {
        this.announce((this.dataset.announceUpdated || '').replace('[[quantity]]', quantity));
        this.dispatchEvent(new CustomEvent('oilogic:cart:updated', { bubbles: true, composed: true, detail: { cart } }));
      }
    } catch (error) {
      // The DOM swap on success never happens here, so the quantity selector
      // that set data-busy is still around — clear it or the spinner sticks.
      this.querySelector(`oilogic-quantity-selector[data-key="${key}"]`)?.removeAttribute('data-busy');
      this.dispatchEvent(new CustomEvent('oilogic:cart:error', { bubbles: true, composed: true, detail: { error } }));
      this.announce(this.dataset.announceError);
    }
  }

  removeLineItem(key) {
    if (key) this.updateLineItem(key, 0);
  }
}

customElements.define('oilogic-cart-drawer', OilogicCartDrawer);
})();
